import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Tags,
  Ticket,
  Users,
  MessageSquare,
  Settings,
  Bell,
  Search,
  Moon,
  Sun,
  Menu,
  X,
  Store,
  Link as LinkIcon,
  FileText,
  ChevronDown,
  MessageCircle,
  Star,
  HelpCircle,
  ChevronRight,
  ShoppingBag,
  Sparkles,
  Shield,
  DollarSign,
  Layout,
  CreditCard,
  Truck,
  Share2,
  Cloud,
  Wand2,
  LogOut,
  UserCircle,
} from 'lucide-react';
import { Logo } from '../components/icons/Logo';
import { useAuth } from '../contexts/AuthContext';
import { db, Quote } from '../services/backend';

const AdminLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [recentQuotes, setRecentQuotes] = useState<Quote[]>([]);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const currentRoute = `${location.pathname}${location.search}`;

  useEffect(() => {
    const fetchQuotes = async () => {
      const quotes = await db.getQuotes();
      // Filter pending quotes and sort by date desc
      const pending = quotes
        .filter((q) => q.status === 'pending')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5);
      setRecentQuotes(pending);
    };
    fetchQuotes();

    // Poll every minute
    const interval = setInterval(fetchQuotes, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const isPathActive = (path: string) => {
    if (path === '#') return false;
    if (path.includes('?')) return currentRoute === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const menuItems = [
    // Dashboard
    {
      icon: <LayoutDashboard size={20} />,
      label: 'Dashboard',
      path: '/admin',
      roles: ['master', 'admin', 'editor', 'viewer'],
    },

    // Aparência
    {
      icon: <Store size={20} />,
      label: 'Aparência',
      path: '#',
      roles: ['master', 'admin'],
      children: [
        {
          icon: <Store size={18} />,
          label: 'Loja Virtual',
          path: '/admin/store-layout',
          roles: ['master', 'admin'],
        },
        {
          icon: <LinkIcon size={18} />,
          label: 'Menus',
          path: '/admin/menus',
          roles: ['master', 'admin'],
        },
      ],
    },

    // Catálogo
    {
      icon: <Package size={20} />,
      label: 'Catálogo',
      path: '#',
      roles: ['master', 'admin', 'editor'],
      children: [
        {
          icon: <Package size={18} />,
          label: 'Produtos',
          path: '/admin/products',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <Tags size={18} />,
          label: 'Categorias',
          path: '/admin/categories',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <Package size={18} />,
          label: 'Kits',
          path: '/admin/kits',
          roles: ['master', 'admin', 'editor'],
        },
      ],
    },

    // Interações
    {
      icon: <MessageCircle size={20} />,
      label: 'Interações',
      path: '#',
      roles: ['master', 'admin', 'editor'],
      children: [
        {
          icon: <Star size={18} />,
          label: 'Avaliações',
          path: '/admin/reviews',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <HelpCircle size={18} />,
          label: 'Perguntas',
          path: '/admin/questions',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <Bell size={18} />,
          label: 'Newsletter',
          path: '/admin/newsletter',
          roles: ['master', 'admin'],
        },
      ],
    },

    // Vendas & Clientes
    {
      icon: <ShoppingBag size={20} />,
      label: 'Vendas',
      path: '#',
      roles: ['master', 'admin', 'viewer'],
      children: [
        {
          icon: <DollarSign size={18} />,
          label: 'Pedidos',
          path: '/admin/sales',
          roles: ['master', 'admin', 'viewer'],
        },
        {
          icon: <Users size={18} />,
          label: 'Clientes',
          path: '/admin/customers',
          roles: ['master', 'admin', 'viewer'],
        },
        {
          icon: <Users size={18} />,
          label: 'Afiliados',
          path: '/admin/affiliates',
          roles: ['master', 'admin'],
        },
        {
          icon: <Ticket size={18} />,
          label: 'Cupons',
          path: '/admin/coupons',
          roles: ['master', 'admin'],
        },
      ],
    },

    // Conteúdo
    {
      icon: <FileText size={20} />,
      label: 'Conteúdo',
      path: '#',
      roles: ['master', 'admin', 'editor'],
      children: [
        {
          icon: <FileText size={18} />,
          label: 'Blog',
          path: '/admin/blog',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <MessageCircle size={18} />,
          label: 'Stories e Lives',
          path: '/admin/stories-media',
          roles: ['master', 'admin', 'editor'],
        },
        {
          icon: <Layout size={18} />,
          label: 'Páginas (CMS)',
          path: '/admin/pages',
          roles: ['master', 'admin'],
        },
      ],
    },

    // IA & Automação
    {
      icon: <Sparkles size={20} />,
      label: 'IA Generator',
      path: '/admin/ai-update',
      roles: ['master', 'admin'],
    },

    // Configurações
    {
      icon: <Settings size={20} />,
      label: 'Configurações',
      path: '#',
      roles: ['master', 'admin'],
      children: [
        {
          icon: <Settings size={18} />,
          label: 'Loja',
          path: '/admin/settings?tab=general',
          roles: ['master', 'admin'],
        },
        {
          icon: <MessageCircle size={18} />,
          label: 'WhatsApp',
          path: '/admin/settings?tab=whatsapp',
          roles: ['master', 'admin'],
        },
        {
          icon: <CreditCard size={18} />,
          label: 'Pagamentos',
          path: '/admin/settings?tab=payments',
          roles: ['master', 'admin'],
        },
        {
          icon: <Truck size={18} />,
          label: 'Frete',
          path: '/admin/settings?tab=shipping',
          roles: ['master', 'admin'],
        },
        {
          icon: <Share2 size={18} />,
          label: 'Integrações',
          path: '/admin/settings?tab=integrations',
          roles: ['master', 'admin'],
        },
        {
          icon: <Cloud size={18} />,
          label: 'CDN',
          path: '/admin/cloudinary-migration',
          roles: ['master', 'admin'],
        },
        {
          icon: <Users size={18} />,
          label: 'Usuários',
          path: '/admin/users',
          roles: ['master', 'admin'],
        },
        {
          icon: <Shield size={18} />,
          label: 'Permissões',
          path: '/admin/permissions',
          roles: ['master'],
        },
      ],
    },
  ];

  const normalizeMenuLabel = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const topLevelMenuOrder = [
    'dashboard',
    'vendas',
    'catalogo',
    'aparencia',
    'conteudo',
    'interacoes',
    'ia generator',
    'configuracoes',
  ];

  const childMenuOrderBySection: Record<string, string[]> = {
    vendas: ['pedidos', 'clientes', 'afiliados', 'cupons'],
    catalogo: ['produtos', 'categorias', 'kits'],
    aparencia: ['loja virtual', 'menus'],
    conteudo: ['blog', 'stories e lives', 'paginas (cms)'],
    interacoes: ['perguntas', 'avaliacoes', 'newsletter'],
    configuracoes: [
      'loja',
      'whatsapp',
      'pagamentos',
      'frete',
      'integracoes',
      'cdn',
      'usuarios',
      'permissoes',
    ],
  };

  const getOrderIndex = (value: string, orderedValues: string[]) => {
    const index = orderedValues.indexOf(normalizeMenuLabel(value));
    return index === -1 ? orderedValues.length : index;
  };

  const filteredMenuItems = menuItems
    .filter((item) => user && item.roles.includes(user.role))
    .map((item) => ({
      ...item,
      children: item.children
        ? [...item.children]
            .filter((child) => user && child.roles.includes(user.role))
            .sort((left, right) => {
              const sectionKey = normalizeMenuLabel(item.label);
              const orderForSection = childMenuOrderBySection[sectionKey] || [];
              return (
                getOrderIndex(left.label, orderForSection) -
                getOrderIndex(right.label, orderForSection)
              );
            })
        : undefined,
    }))
    .filter((item) => !item.children || item.children.length > 0)
    .sort(
      (left, right) =>
        getOrderIndex(left.label, topLevelMenuOrder) -
        getOrderIndex(right.label, topLevelMenuOrder)
    );

  useEffect(() => {
    const activeParents = filteredMenuItems
      .filter((item) => item.children?.some((child) => isPathActive(child.path)))
      .map((item) => item.label);

    if (activeParents.length === 0) return;

    setExpandedMenus((prev) => Array.from(new Set([...prev, ...activeParents])));
  }, [currentRoute]);

  const shellClasses = isDarkMode
    ? 'dark bg-[#140f15] text-white'
    : 'bg-transparent text-[var(--admin-text)]';
  const sidebarClasses = isDarkMode
    ? 'bg-[#1b151b] border-white/10'
    : 'bg-[rgba(255,252,251,0.92)] border-[var(--admin-border)] shadow-[0_24px_60px_rgba(82,42,64,0.08)] backdrop-blur-xl';
  const headerClasses = isDarkMode
    ? 'bg-[#1b151b]/95 border-white/10'
    : 'bg-[rgba(255,252,251,0.82)] border-[var(--admin-border)] backdrop-blur-xl';
  const utilityButtonClasses = isDarkMode
    ? 'text-gray-300 hover:bg-white/10'
    : 'text-[var(--admin-muted)] hover:bg-[#faeff4] hover:text-veraz-pink';
  const dropdownClasses = isDarkMode
    ? 'bg-[#1f181f] border-white/10'
    : 'bg-[rgba(255,252,251,0.96)] border-[var(--admin-border)] shadow-[0_18px_50px_rgba(82,42,64,0.12)] backdrop-blur-xl';

  return (
    <div className={`admin-theme h-screen overflow-hidden flex ${shellClasses}`}>
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-20'
        } ${sidebarClasses} border-r flex flex-col`}
      >
        <div className="h-16 flex items-center justify-center border-b border-gray-200 dark:border-white/10">
          <div className={`flex items-center ${!isSidebarOpen && 'lg:justify-center'}`}>
            {isSidebarOpen ? (
              <Logo aria-label="Veraz Cosméticos" className="h-8 w-auto text-veraz-pink" />
            ) : (
              <Logo aria-label="A" className="h-8 w-8 object-contain lg:hidden text-veraz-pink" />
            )}
            <span
              className={`ml-2 text-[11px] uppercase tracking-[0.28em] text-[var(--admin-muted)] ${!isSidebarOpen && 'lg:hidden'}`}
            >
              Admin
            </span>
            {!isSidebarOpen && (
              <span className="hidden lg:block font-serif font-bold text-veraz-gold text-xl">
                A
              </span>
            )}
          </div>
        </div>

        <nav className="flex-1 py-6 space-y-1 px-3 overflow-y-auto">
          {filteredMenuItems.map((item) => {
            const isExpanded = expandedMenus.includes(item.label);
            const isChildActive = Boolean(item.children?.some((child) => isPathActive(child.path)));

            return (
              <div key={item.label}>
                {item.children ? (
                  <>
                    <button
                      onClick={() => toggleMenu(item.label)}
                      aria-expanded={isExpanded}
                      className={`w-full flex items-center justify-between px-3 py-3 rounded-2xl border transition-all duration-200 group ${
                        isExpanded || isChildActive
                          ? isDarkMode
                            ? 'bg-white/10 border-white/10 text-white'
                            : 'bg-[linear-gradient(135deg,rgba(255,0,128,0.08),rgba(201,162,39,0.12))] border-[var(--admin-border-strong)] text-[var(--admin-heading)] shadow-sm'
                          : isDarkMode
                            ? 'border-transparent text-gray-300 hover:bg-white/5 hover:text-white'
                            : 'border-transparent text-[var(--admin-muted)] hover:bg-[#fbf1f5] hover:text-[var(--admin-heading)]'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="shrink-0">{item.icon}</span>
                        <span
                          className={`ml-3 font-medium whitespace-nowrap transition-opacity duration-300 ${!isSidebarOpen ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : 'opacity-100'}`}
                        >
                          {item.label}
                        </span>
                      </div>
                      {isSidebarOpen &&
                        (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                    </button>

                    {isExpanded && isSidebarOpen && (
                      <div className="ml-4 mt-2 space-y-1 border-l-2 border-[var(--admin-border-strong)] pl-3">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center px-3 py-2 rounded-xl border transition-colors text-sm ${
                              isPathActive(child.path)
                                ? isDarkMode
                                  ? 'border-white/10 bg-white/10 text-veraz-gold font-medium'
                                  : 'border-[#f0d4df] bg-white/80 text-veraz-pink font-semibold'
                                : isDarkMode
                                  ? 'border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                                  : 'border-transparent text-[var(--admin-muted)] hover:bg-white/70 hover:text-[var(--admin-heading)]'
                            }`}
                          >
                            <span className="shrink-0">{child.icon}</span>
                            <span className="ml-3">{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center px-3 py-3 rounded-2xl border transition-all duration-200 group ${
                      isPathActive(item.path)
                        ? isDarkMode
                          ? 'border-white/10 bg-white/10 text-veraz-gold'
                          : 'border-[var(--admin-border-strong)] bg-[linear-gradient(135deg,rgba(255,0,128,0.08),rgba(201,162,39,0.12))] text-veraz-pink shadow-sm'
                        : isDarkMode
                          ? 'border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                          : 'border-transparent text-[var(--admin-muted)] hover:bg-[#fbf1f5] hover:text-[var(--admin-heading)]'
                    }`}
                    title={item.label}
                  >
                    <span className="shrink-0">{item.icon}</span>
                    <span
                      className={`ml-3 font-medium whitespace-nowrap transition-opacity duration-300 ${
                        !isSidebarOpen ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : 'opacity-100'
                      }`}
                    >
                      {item.label}
                    </span>
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        <div className="px-3 py-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center px-3 py-3 rounded-2xl border transition-colors group ${
              isDarkMode
                ? 'border-transparent text-gray-400 hover:bg-white/5 hover:text-white'
                : 'border-transparent text-[var(--admin-muted)] hover:bg-[#fbf1f5] hover:text-veraz-pink'
            }`}
            title="Ir para Loja"
          >
            <Store size={20} className="shrink-0" />
            <span
              className={`ml-3 font-medium whitespace-nowrap transition-opacity duration-300 ${
                !isSidebarOpen ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : 'opacity-100'
              }`}
            >
              Ir para Loja
            </span>
          </a>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center w-full text-[var(--admin-muted)] hover:text-red-500 transition-colors p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/10"
            title="Sair do sistema"
          >
            <LogOut size={20} />
            <span className={`ml-3 font-medium ${!isSidebarOpen && 'lg:hidden'}`}>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className={`h-16 flex items-center justify-between px-4 border-b ${headerClasses}`}>
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`p-2 rounded-xl lg:hidden ${utilityButtonClasses}`}
            >
              <Menu size={20} />
            </button>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`hidden lg:block p-2 rounded-xl ${utilityButtonClasses}`}
            >
              {isSidebarOpen ? <Menu size={20} /> : <Menu size={20} />}
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-full ${utilityButtonClasses}`}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className={`p-2 rounded-full relative ${utilityButtonClasses}`}
              >
                <Bell size={20} />
                {recentQuotes.length > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-veraz-pink ring-2 ring-white/80"></span>
                )}
              </button>

              {notificationsOpen && (
                <div
                  className={`absolute right-0 mt-2 w-80 rounded-2xl py-1 z-50 border ${dropdownClasses}`}
                >
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Notificações
                    </h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {recentQuotes.length > 0 ? (
                      recentQuotes.map((quote) => (
                        <Link
                          key={quote.id}
                          to={`/admin/quotes`}
                          onClick={() => setNotificationsOpen(false)}
                          className={`block px-4 py-3 transition-colors ${
                            isDarkMode ? 'hover:bg-white/5' : 'hover:bg-[#fbf1f5]'
                          }`}
                        >
                          <div className="flex items-start">
                            <ShoppingBag className="h-5 w-5 text-veraz-gold mt-0.5" />
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                Nova Cotação de {quote.customerName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {new Date(quote.createdAt).toLocaleDateString()} às{' '}
                                {new Date(quote.createdAt).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        </Link>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                        Nenhuma nova notificação
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 rounded-b-2xl">
                    <Link
                      to="/admin/quotes"
                      onClick={() => setNotificationsOpen(false)}
                      className="block text-center text-xs font-medium text-veraz-gold hover:text-veraz-brown"
                    >
                      Ver todas as cotações
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                className={`flex items-center space-x-2 rounded-full p-1 transition-colors ${
                  isDarkMode ? 'hover:bg-white/10' : 'hover:bg-[#faeff4]'
                }`}
              >
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name || 'Admin'}
                    className="w-8 h-8 rounded-full object-cover shadow-[0_10px_22px_rgba(201,162,39,0.18)]"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[linear-gradient(135deg,#e5c65e_0%,#c99f30_50%,#a47c1f_100%)] text-white flex items-center justify-center font-bold shadow-[0_10px_22px_rgba(201,162,39,0.28)]">
                    {user?.name?.charAt(0) || 'A'}
                  </div>
                )}
                <span className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {user?.name || 'Admin'}
                </span>
                <ChevronDown size={14} className="text-[var(--admin-muted)]" />
              </button>

              {isProfileMenuOpen && (
                <div
                  className={`absolute right-0 mt-2 w-48 rounded-2xl py-1 z-50 border ${dropdownClasses}`}
                >
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user?.email}
                    </p>
                  </div>
                  <a
                    href="/admin/settings?tab=general"
                    className={`flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 ${
                      isDarkMode ? 'hover:bg-white/5' : 'hover:bg-[#fbf1f5]'
                    }`}
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    <UserCircle size={16} className="mr-2" />
                    Meu Perfil
                  </a>
                  <button
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
                  >
                    <LogOut size={16} className="mr-2" />
                    Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-[#2d1b25]/45 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default AdminLayout;
